import {
  keepPreviousData,
  noop,
  queryOptions,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { useCallback } from 'react'

import { livePolling } from '@/app/query-client'
import { heatmapKeys } from '@/features/heatmap/api/keys'
import { heatmapService } from '@/features/heatmap/api/heatmapService'
import { useMarketStatus } from '@/hooks/useMarketStatus'
import type { Heatmap, HeatmapQuery } from '@/schemas/heatmap'

/**
 * A map together with the query it answers.
 *
 * `keepPreviousData` hands a drill-in the map from the level above while the
 * names load, and the screen has to know that is what it is holding: the URL
 * already says Banking, the tiles are still sectors, and a stale sector tile
 * that decided its link from the URL would send the member to
 * `/stocks/banking`. Carrying the query in the cached value answers "which
 * depth are these tiles?" from the data itself.
 */
export interface HeatmapView {
  query: HeatmapQuery
  map: Heatmap
}

export const heatmapQueryOptions = (query: HeatmapQuery) =>
  queryOptions({
    queryKey: heatmapKeys.map(query),
    queryFn: async (): Promise<HeatmapView> => ({ query, map: await heatmapService.heatmap(query) }),
  })

/**
 * The heatmap, at whichever depth the URL asks for.
 *
 * `keepPreviousData` matters more here than on a table: without it, drilling
 * into a sector unmounts every tile and the map flashes empty before the
 * nineteen names arrive, which reads as a page that lost its data rather than
 * one that is zooming in.
 */
export const useHeatmap = (query: HeatmapQuery): UseQueryResult<HeatmapView> => {
  const { isOpen } = useMarketStatus()

  return useQuery({
    ...heatmapQueryOptions(query),
    refetchInterval: livePolling(isOpen),
    placeholderData: keepPreviousData,
  })
}

/**
 * Starts a map before it is asked for — from a tile's `onTileIntent`, so a
 * sector the pointer is resting on is usually in the cache by the time it is
 * pressed and the drill-in paints at once.
 *
 * The same guard `usePrefetch` uses: only a key with no cache entry is
 * fetched. A map already cached — fresh or stale — is left to the hook that
 * reads it, which paints it immediately and refreshes it in the background.
 */
export const useHeatmapPrefetch = (): ((query: HeatmapQuery) => void) => {
  const client = useQueryClient()

  return useCallback(
    (query: HeatmapQuery) => {
      const options = heatmapQueryOptions(query)
      if (client.getQueryState(options.queryKey) !== undefined) return
      // A failure is the drill-in's to show if the member does press the
      // tile: it mounts on the errored entry and retries through `useHeatmap`.
      client.query(options).catch(noop)
    },
    [client],
  )
}
